import { useEffect, useState } from 'react';
import '../../styles/admin.css';
import { fetchMessagesAdmin, updateMessage, deleteMessage } from '../../lib/messages';
import { pb } from '../../lib/pb';


import { Mail, Phone, User, Calendar, Tag, Trash2, Archive, CheckCircle, RefreshCcw, X, ChevronRight, MessageSquare, Info } from 'lucide-react';

export default function AdminMessages() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);

  // --- MOBILE DETECTION ---
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  useEffect(() => {
    const sync = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', sync); return () => window.removeEventListener('resize', sync);
  }, []);

  async function load() {
    setLoading(true); setError('');
    try {
      const res = await fetchMessagesAdmin(page, 30, filterStatus);
      setItems(res.items);
      setTotalPages(res.totalPages || 1);
    } catch (e) {
      setError(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [page, filterStatus]);

  useEffect(() => {
    let unsub = null;
    (async () => {
      try { unsub = await pb.collection('messages').subscribe('*', () => load()); } catch(e) {}
    })();
    return () => { try { if(unsub) pb.collection('messages').unsubscribe('*'); } catch(e) {} };
  }, []);

  async function changeStatus(message, status) {
    try {
      await updateMessage(message.id, { status });
      await load();
      if (selected && selected.id === message.id) setSelected({ ...selected, status });
    } catch(e) { setError("Erreur de mise à jour"); }
  }

  async function handleDelete(message) {
    if(!window.confirm(`Supprimer le message ?`)) return;
    try {
      await deleteMessage(message.id);
      if(selected && selected.id === message.id) setSelected(null);
      await load();
    } catch(e) { setError("Erreur de suppression"); }
  }

  function formatDate(dateStr) {
    if(!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  }

  return (
    <div className="messages-page">
      {/* HEADER */}
      <header className="messages-header">
        <div className="header-text">
          <h1>Messagerie Cliente</h1>
          <p className="muted">Gérez vos demandes de contact et devis entants.</p>
        </div>
        <div className="header-tabs">
           {['all', 'new', 'read', 'archived'].map(s => (
             <button key={s} className={filterStatus === s ? 'active' : ''} onClick={() => { setPage(1); setFilterStatus(s); }}>
                {s === 'all' ? 'Tous' : s === 'new' ? 'Nouveaux' : s === 'read' ? 'Lus' : 'Archives'}
             </button>
           ))}
        </div>
      </header>

      {/* CONTENT AREA */}
      <main className="messages-main">
        {loading && <div className="loading-state">Récupération des messages...</div>}
        
        <div className={`messages-layout ${selected ? 'has-reader' : ''}`}>
           {/* LIST AREA */}
           <div className="messages-list-pane">
              {items.map(m => (
                <div key={m.id} className={`msg-card ${m.status} ${selected?.id === m.id ? 'selected' : ''}`} onClick={() => setSelected(m)}>
                  <div className="msg-avatar">
                     {m.name ? m.name.charAt(0).toUpperCase() : <User size={18}/>}
                     {m.status === 'new' && <div className="new-dot" />}
                  </div>
                  <div className="msg-body">
                    <div className="msg-top">
                       <span className="msg-name">{m.name || 'Anonyme'}</span>
                       <span className="msg-date">{formatDate(m.created)}</span>
                    </div>
                    <div className="msg-subject">{m.subject || 'Pas d\'objet'}</div>
                    <p className="msg-preview">{m.message?.substring(0, 60)}...</p>
                  </div>
                  <ChevronRight size={16} className="msg-arrow" />
                </div>
              ))}
              {items.length === 0 && !loading && <div className="empty-state">Aucun message dans cette catégorie.</div>}
              
              {totalPages > 1 && (
                <div className="pagination">
                   <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}>Prec</button>
                   <span>{page} / {totalPages}</span>
                   <button onClick={() => setPage(p => p+1)} disabled={page >= totalPages}>Suiv</button>
                </div>
              )}
           </div>

           {/* READER AREA (Desktop) */}
           {selected && !isMobile && (
             <div className="messages-reader-pane">
                <div className="reader-header">
                   <div className="reader-meta">
                      <h2>{selected.subject || 'Message sans objet'}</h2>
                      <div className="reader-sender">
                        <span className="name">{selected.name}</span>
                        <span className="email"><Mail size={12}/> {selected.email}</span>
                        {selected.phone && <span className="phone"><Phone size={12}/> {selected.phone}</span>}
                      </div>
                   </div>
                   <button className="btn-close-reader" onClick={() => setSelected(null)}><X size={20}/></button>
                </div>

                <div className="reader-content">
                   <div className="reader-scroll">
                      <div className="date-badge"><Calendar size={12}/> Reçu le {formatDate(selected.created)}</div>
                      <div className="message-text">{selected.message}</div>
                   </div>
                </div>

                <div className="reader-footer">
                   <div className="footer-acts">
                     <button className="act-btn ok" onClick={() => changeStatus(selected, 'read')} title="Marquer lu"><CheckCircle size={18}/></button>
                     <button className="act-btn warn" onClick={() => changeStatus(selected, 'archived')} title="Archiver"><Archive size={18}/></button>
                     <button className="act-btn del" onClick={() => handleDelete(selected)} title="Supprimer"><Trash2 size={18}/></button>
                   </div>
                </div>
             </div>
           )}
        </div>
      </main>

      {/* MOBILE READER (Modal) */}
      {selected && isMobile && (
        <div className="mobile-reader-overlay">
           <div className="mobile-reader-content">
              <header className="mobile-r-top">
                 <button className="back-btn" onClick={() => setSelected(null)}><X size={24}/></button>
                 <div className="r-acts">
                    <button onClick={() => { changeStatus(selected, 'archived'); setSelected(null); }}><Archive size={20}/></button>
                    <button onClick={() => { handleDelete(selected); setSelected(null); }} className="del"><Trash2 size={20}/></button>
                 </div>
              </header>
              <div className="mobile-r-body">
                 <div className="r-info">
                   <div className="r-avatar">{selected.name?.charAt(0)}</div>
                   <div className="r-names">
                     <h3>{selected.name}</h3>
                     <p>{selected.email}</p>
                   </div>
                 </div>
                 <div className="r-subject">{selected.subject}</div>
                 <div className="r-date">{formatDate(selected.created)}</div>
                 <div className="r-text">{selected.message}</div>
              </div>
              {selected.phone && (
                <a href={`tel:${selected.phone}`} className="btn-call-mobile"><Phone size={20}/> Appeler le client</a>
              )}
           </div>
        </div>
      )}

      <style>{`
        .messages-page { display: flex; flex-direction: column; gap: 20px; animation: fadeIn 0.4s ease; height: calc(100vh - 120px); }
        
        .messages-header { display: flex; justify-content: space-between; align-items: flex-end; }
        .messages-header h1 { font-size: 28px; font-weight: 900; margin: 0; color: #0f172a; }
        
        .header-tabs { background: #f1f5f9; padding: 4px; border-radius: 14px; display: flex; gap: 4px; }
        .header-tabs button { border: none; background: none; padding: 8px 16px; border-radius: 10px; font-weight: 800; font-size: 13px; color: #64748b; cursor: pointer; transition: all 0.2s; }
        .header-tabs button.active { background: #fff; color: #0f172a; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); }

        .messages-main { flex: 1; overflow: hidden; background: #fff; border-radius: 28px; border: 1px solid var(--admin-border); }
        .messages-layout { display: flex; height: 100%; }
        
        /* LIST */
        .messages-list-pane { width: 100%; overflow-y: auto; display: flex; flex-direction: column; border-right: 1px solid #f1f5f9; }
        .has-reader .messages-list-pane { width: 400px; }
        
        .msg-card { padding: 20px; border-bottom: 1px solid #f1f5f9; display: flex; gap: 15px; cursor: pointer; transition: all 0.2s; position: relative; align-items: center; }
        .msg-card:hover { background: #f8fafc; }
        .msg-card.selected { background: #f1f5f9; }
        .msg-card.new { background: #f0f7ff; }
        .msg-card.new:hover { background: #e0efff; }

        .msg-avatar { width: 48px; height: 48px; border-radius: 14px; background: #fff; border: 1px solid #e2e8f0; display: flex; align-items: center; justify-content: center; font-weight: 900; position: relative; flex-shrink: 0; }
        .new-dot { position: absolute; top: -4px; right: -4px; width: 12px; height: 12px; background: #3b82f6; border-radius: 50%; border: 2px solid #fff; }
        
        .msg-body { flex: 1; overflow: hidden; }
        .msg-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2px; }
        .msg-name { font-weight: 800; font-size: 14px; color: #0f172a; }
        .msg-date { font-size: 11px; font-weight: 600; color: #94a3b8; }
        .msg-subject { font-size: 13px; font-weight: 700; color: #64748b; margin-bottom: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .msg-preview { font-size: 12px; color: #94a3b8; margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .msg-arrow { color: #f1f5f9; transition: transform 0.2s; }
        .msg-card:hover .msg-arrow { transform: translateX(2px); color: var(--admin-primary); }

        /* READER */
        .messages-reader-pane { flex: 1; display: flex; flex-direction: column; background: #f8fafc; }
        .reader-header { padding: 25px; background: #fff; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: flex-start; }
        .reader-meta h2 { margin: 0 0 10px 0; font-size: 20px; font-weight: 900; color: #0f172a; }
        .reader-sender { display: flex; gap: 15px; align-items: center; }
        .reader-sender span { font-size: 13px; font-weight: 700; color: #94a3b8; display: flex; align-items: center; gap: 5px; }
        .reader-sender .name { color: #0f172a; }
        
        .btn-close-reader { border: none; background: #f1f5f9; color: #94a3b8; padding: 8px; border-radius: 12px; cursor: pointer; }

        .reader-content { flex: 1; padding: 30px; overflow-y: auto; }
        .reader-scroll { max-width: 700px; margin: 0 auto; }
        .date-badge { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; font-size: 12px; font-weight: 700; color: #64748b; margin-bottom: 20px; }
        .message-text { font-size: 15px; line-height: 1.6; color: #334155; white-space: pre-wrap; font-weight: 500; }

        .reader-footer { padding: 20px; background: #fff; border-top: 1px solid #f1f5f9; }
        .footer-acts { display: flex; gap: 10px; justify-content: center; }
        .act-btn { width: 44px; height: 44px; border-radius: 12px; border: 1px solid #f1f5f9; background: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
        .act-btn.ok { color: #10b981; }
        .act-btn.warn { color: #f59e0b; }
        .act-btn.del { color: #f43f5e; }
        .act-btn:hover { transform: translateY(-2px); box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); }

        /* MOBILE READER */
        .mobile-reader-overlay { position: fixed; inset: 0; background: #fff; z-index: 30000; display: flex; flex-direction: column; }
        .mobile-reader-content { display: flex; flex-direction: column; height: 100%; }
        .mobile-r-top { padding: 15px; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #f1f5f9; margin-top: 30px; }
        .back-btn { border: none; background: none; color: #0f172a; cursor: pointer; }
        .r-acts { display: flex; gap: 15px; }
        .r-acts button { border: none; background: none; color: #64748b; cursor: pointer; }
        .r-acts .del { color: #f43f5e; }

        .mobile-r-body { flex: 1; padding: 20px; overflow-y: auto; }
        .r-info { display: flex; gap: 15px; align-items: center; margin-bottom: 25px; }
        .r-avatar { width: 50px; height: 50px; background: #f1f5f9; border-radius: 15px; display: flex; align-items: center; justify-content: center; font-weight: 900; color: #0f172a; font-size: 20px; }
        .r-names h3 { margin: 0; font-size: 18px; font-weight: 900; }
        .r-names p { margin: 0; font-size: 13px; color: #94a3b8; font-weight: 700; }
        .r-subject { font-size: 22px; font-weight: 900; color: #0f172a; margin-bottom: 10px; }
        .r-date { font-size: 12px; font-weight: 700; color: #cbd5e1; margin-bottom: 20px; border-bottom: 1px solid #f1f5f9; padding-bottom: 15px; }
        .r-text { font-size: 16px; line-height: 1.6; color: #334155; white-space: pre-wrap; font-weight: 500; }
        
        .btn-call-mobile { margin: 20px; background: #0f172a; color: #fff; padding: 16px; border-radius: 16px; text-decoration: none; display: flex; align-items: center; justify-content: center; gap: 12px; font-weight: 800; }

        .pagination { padding: 15px; border-top: 1px solid #f1f5f9; display: flex; justify-content: center; align-items: center; gap: 15px; background: #fff; }
        .pagination button { padding: 6px 12px; border: 1px solid #e2e8f0; border-radius: 10px; background: #fff; font-weight: 700; cursor: pointer; }
        .pagination span { font-size: 13px; font-weight: 700; color: #94a3b8; }

        @media (max-width: 900px) {
          .messages-page { height: auto; }
          .messages-header { flex-direction: column; align-items: flex-start; gap: 15px; }
          .header-tabs { width: 100%; justify-content: space-between; }
          .messages-main { border: none; border-radius: 0; }
          .msg-card { padding: 15px; }
          .msg-avatar { width: 44px; height: 44px; }
          .pagination { position: sticky; bottom: 0; }
        }
        
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
